"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import {
  APIProvider,
  Map,
  AdvancedMarker,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps"
import type { SelectedLocation } from "@/lib/types"
import { MapPin, Search, Navigation, Crosshair } from "lucide-react"
import { cn } from "@/lib/utils"

const KENYA_CENTER = { lat: -1.2921, lng: 36.8219 }
const MAP_ID = "landrisk-map"

interface GoogleMapWrapperProps {
  onLocationSelect: (location: SelectedLocation) => void
}

export function GoogleMapWrapper({ onLocationSelect }: GoogleMapWrapperProps) {
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || ""

  return (
    <APIProvider apiKey={apiKey}>
      <MapContent onLocationSelect={onLocationSelect} />
    </APIProvider>
  )
}

/* ------------ Autocomplete (new web component API) ------------ */
function PlaceAutocomplete({
  onPlaceSelect,
}: {
  onPlaceSelect: (place: google.maps.places.Place | null) => void
}) {
  useMapsLibrary("places")

  async function handlePlaceSelect(place: google.maps.places.Place) {
    await place.fetchFields({
      fields: ["displayName", "formattedAddress", "location", "viewport"],
    })
    onPlaceSelect(place)
  }

  return (
    <div className="w-full [&_gmp-place-autocomplete]:w-full [&_input]:h-10 [&_input]:w-full [&_input]:rounded-md [&_input]:border [&_input]:border-input [&_input]:bg-background [&_input]:px-3 [&_input]:py-2 [&_input]:text-sm [&_input]:text-foreground [&_input]:placeholder:text-muted-foreground [&_input]:focus-visible:outline-none [&_input]:focus-visible:ring-2 [&_input]:focus-visible:ring-ring">
      <gmp-place-autocomplete
        country="ke"
        placeholder="Search for an area (e.g., Kitengela, Ruiru, Thika)..."
        ongmp-select={(ev: PlaceAutoSelectEvent) =>
          void handlePlaceSelect(ev.placePrediction.toPlace())
        }
        ongmp-placeselect={(ev: PlaceAutoPlaceSelectEvent) =>
          void handlePlaceSelect(ev.place)
        }
      />
    </div>
  )
}

/* eslint-disable @typescript-eslint/no-explicit-any */
type PlaceAutoSelectEvent = any
type PlaceAutoPlaceSelectEvent = any

declare module "react" {
  namespace JSX {
    interface IntrinsicElements {
      "gmp-place-autocomplete": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          country?: string
          placeholder?: string
          ongmpselect?: (ev: any) => void
          "ongmp-select"?: (ev: any) => void
          "ongmp-placeselect"?: (ev: any) => void
        },
        HTMLElement
      >
    }
  }
}

/* ------------ Main Map Content ------------ */
function MapContent({ onLocationSelect }: GoogleMapWrapperProps) {
  const map = useMap()
  const geocodingLib = useMapsLibrary("geocoding")

  const [marker, setMarker] = useState<google.maps.LatLngLiteral | null>(null)
  const [activeTab, setActiveTab] = useState<"search" | "pin" | "gps">("search")
  const [gpsLat, setGpsLat] = useState("")
  const [gpsLng, setGpsLng] = useState("")
  const [isReversing, setIsReversing] = useState(false)

  const geocoderRef = useRef<google.maps.Geocoder | null>(null)

  useEffect(() => {
    if (geocodingLib) {
      geocoderRef.current = new geocodingLib.Geocoder()
    }
  }, [geocodingLib])

  /* ---- Handle autocomplete place selection ---- */
  const handlePlaceSelect = useCallback(
    (place: google.maps.places.Place | null) => {
      if (!place?.location) return
      const lat = place.location.lat()
      const lng = place.location.lng()
      const pos = { lat, lng }
      setMarker(pos)
      map?.panTo(pos)
      map?.setZoom(14)
      if (place.viewport) {
        map?.fitBounds(place.viewport)
      }
      reverseGeocode(lat, lng)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [map]
  )

  const reverseGeocode = useCallback(
    async (lat: number, lng: number) => {
      if (!geocoderRef.current) return
      setIsReversing(true)
      try {
        const response = await geocoderRef.current.geocode({
          location: { lat, lng },
        })
        if (response.results[0]) {
          const result = response.results[0]
          const components = result.address_components
          const county =
            components.find((c) =>
              c.types.includes("administrative_area_level_1")
            )?.long_name || "Unknown County"
          const adminArea =
            components.find(
              (c) =>
                c.types.includes("administrative_area_level_2") ||
                c.types.includes("locality") ||
                c.types.includes("sublocality")
            )?.long_name || "Unknown Area"
          const areaName =
            components.find(
              (c) =>
                c.types.includes("sublocality") ||
                c.types.includes("locality") ||
                c.types.includes("neighborhood")
            )?.long_name || adminArea

          onLocationSelect({
            lat,
            lng,
            areaName,
            county,
            adminArea,
          })
        }
      } catch {
        onLocationSelect({
          lat,
          lng,
          areaName: "Selected Location",
          county: "Unknown County",
          adminArea: "Unknown Area",
        })
      } finally {
        setIsReversing(false)
      }
    },
    [onLocationSelect]
  )

  const handleMapClick = useCallback(
    (e: { detail: { latLng: { lat: number; lng: number } | null } }) => {
      if (activeTab !== "pin") return
      const lat = e.detail?.latLng?.lat
      const lng = e.detail?.latLng?.lng
      if (lat !== undefined && lng !== undefined) {
        setMarker({ lat, lng })
        reverseGeocode(lat, lng)
      }
    },
    [activeTab, reverseGeocode]
  )

  const handleGpsSubmit = () => {
    const lat = parseFloat(gpsLat)
    const lng = parseFloat(gpsLng)
    if (isNaN(lat) || isNaN(lng) || lat < -5 || lat > 5 || lng < 33 || lng > 42) {
      return
    }
    const pos = { lat, lng }
    setMarker(pos)
    map?.panTo(pos)
    map?.setZoom(14)
    reverseGeocode(lat, lng)
  }

  const handleUseMyLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude
          const lng = position.coords.longitude
          const pos = { lat, lng }
          setMarker(pos)
          map?.panTo(pos)
          map?.setZoom(14)
          reverseGeocode(lat, lng)
        },
        () => {
          // Geolocation denied or unavailable
        }
      )
    }
  }

  const tabs = [
    { id: "search" as const, label: "Search Area", icon: Search },
    { id: "pin" as const, label: "Drop Pin", icon: MapPin },
    { id: "gps" as const, label: "GPS Coords", icon: Navigation },
  ]

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4">
        <div className="flex gap-1 rounded-lg bg-muted p-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                activeTab === tab.id
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <tab.icon className="size-3.5" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {activeTab === "search" && (
          <PlaceAutocomplete onPlaceSelect={handlePlaceSelect} />
        )}

        {activeTab === "pin" && (
          <div className="flex items-center gap-3">
            <p className="text-sm text-muted-foreground">
              Click anywhere on the map below to drop a pin on your parcel
            </p>
            <button
              onClick={handleUseMyLocation}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-accent"
            >
              <Crosshair className="size-3.5" />
              Use My Location
            </button>
          </div>
        )}

        {activeTab === "gps" && (
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              type="number"
              step="any"
              placeholder="Latitude (e.g., -1.2921)"
              value={gpsLat}
              onChange={(e) => setGpsLat(e.target.value)}
              className="flex h-10 flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <input
              type="number"
              step="any"
              placeholder="Longitude (e.g., 36.8219)"
              value={gpsLng}
              onChange={(e) => setGpsLng(e.target.value)}
              className="flex h-10 flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <button
              onClick={handleGpsSubmit}
              className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Locate
            </button>
          </div>
        )}
      </div>

      <div className="relative h-[55vh] w-full overflow-hidden rounded-xl border border-border">
        {isReversing && (
          <div className="absolute left-4 top-4 z-10 rounded-md bg-background/90 px-3 py-1.5 text-xs font-medium text-foreground shadow-sm backdrop-blur">
            Identifying location...
          </div>
        )}
        <Map
          defaultCenter={KENYA_CENTER}
          defaultZoom={7}
          mapId={MAP_ID}
          gestureHandling="greedy"
          disableDefaultUI={false}
          mapTypeControl={true}
          zoomControl={true}
          streetViewControl={false}
          fullscreenControl={false}
          onClick={handleMapClick}
          className="size-full"
        >
          {marker && (
            <AdvancedMarker position={marker}>
              <div className="flex flex-col items-center">
                <div className="rounded-full bg-primary p-1.5 shadow-lg">
                  <MapPin className="size-5 text-primary-foreground" />
                </div>
                <div className="mt-0.5 size-2 rounded-full bg-primary/50" />
              </div>
            </AdvancedMarker>
          )}
        </Map>
      </div>
    </div>
  )
}
